import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlassCheers,
  faUsers,
  faEnvelope,
  faCalendarAlt,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import SEO from '../../common/SEO';

const balloonImage = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/balloon.webp';
const balloonBar = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/ballonBar.webp';
const castleGin = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/castleGin.webp';
const cocktail = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/cocktail.webp';
const garnishCocktail = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/garnishCocktail.webp';
const makingCocktail = 'https://BelvoirCastle.b-cdn.net/Balloon%20bar/makingCocktail.webp';

const BalloonBarContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Section = styled.section`
  margin: 6rem 0;
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 2rem;
  text-align: center;
  position: relative;
`;

const DrinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DrinkCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const DrinkImage = styled.div`
  height: 200px;
  background-image: url(\${props => props.image});
  background-size: cover;
  background-position: center;
`;

const DrinkContent = styled.div`
  padding: 2rem;
`;

const DrinkTitle = styled.h3`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const DrinkPrice = styled.span`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: bold;
`;

const DrinkDescription = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.6;
  margin: 1rem 0;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1rem;
  }
`;

const EventsSection = styled.div`
  background: ${props => props.theme.colors.greyBackground};
  padding: 3rem;
  border-radius: 12px;
  margin-top: 3rem;
`;

const EventsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const EventCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const EventTitle = styled.h3`
  font-size: 1.8rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
`;

const EventTime = styled.p`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.6;
  margin: 1rem 0;
`;

const EventImage = styled.img`
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 8px;
  margin-bottom: 1rem;
`;

const EventDescription = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.secondary};
  line-height: 1.6;
  margin: 1rem 0;
  text-align: left;
`;

const ImageHero = styled.div`
  position: relative;
  height: 90vh;
  overflow: hidden;
  background: ${props => props.theme.colors.primary}; // Fallback background
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${balloonImage});
    background-size: cover;
    background-position: center;
    z-index: 1;
  }
`;

const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0.5)
  );
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
`;

const HeroTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 4rem;
  color: white;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  color: white;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  cursor: pointer;
  animation: bounce 2s infinite;

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateX(-50%) translateY(0);
    }
    40% {
      transform: translateX(-50%) translateY(-10px);
    }
    60% {
      transform: translateX(-50%) translateY(-5px);
    }
  }

  svg {
    width: 40px;
    height: 40px;
    fill: none;
    stroke: white;
    stroke-width: 2;
    filter: drop-shadow(0 0 4px rgba(0, 0, 0, 0.3));
    opacity: 0.8;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 1;
    }
  }
`;

const GallerySection = styled.section`
  padding: 6rem 0;
  background: white;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(2, 300px);
  gap: 1.5rem;
  padding: 0 2rem;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 250px);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 200px);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(6, 200px);
  }
`;

const GalleryItem = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  grid-column: ${props => props.$large ? 'span 2' : 'span 1'};
  grid-row: ${props => props.$large ? 'span 2' : 'span 1'};

  &:hover {
    transform: scale(1.02);
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const GalleryOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  padding: 1.5rem;
  text-align: center;
`;

const GalleryTitle = styled.h3`
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const MembershipSection = styled.section`
  background: ${props => props.theme.colors.greyBackground};
  padding: 4rem 0;
  text-align: center;
`;

const MembershipCard = styled.div`
  background: white;
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`;

const MembershipIcon = styled.div`
  width: 80px;
  height: 80px;
  background: ${props => props.theme.colors.accent};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  
  svg {
    color: white;
    font-size: 2.5rem;
  }
`;

const ComingSoonBadge = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  margin-left: 0.5rem;
  vertical-align: middle;
`;

const MembershipButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const IntroText = styled.p`
  font-size: 1.2rem;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const BenefitText = styled.span`
  margin-left: 0.5rem;
`;

const MenuSection = styled.div`
  margin: 5rem 0;
`;

const MenuTabs = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const MenuTab = styled.button`
  background: ${props => props.active ? props.theme.colors.accent : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.primary};
  border: 2px solid ${props => props.active ? props.theme.colors.accent : props.theme.colors.primary};
  padding: 0.8rem 1.5rem;
  margin: 0.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 50px;
  
  &:hover {
    background: ${props => props.active ? props.theme.colors.accent : props.theme.colors.greyBackground};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  margin-top: 3rem;
`;

const MenuCategory = styled.div`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const CategoryTitle = styled.h3`
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.accent};
  padding-bottom: 1rem;
`;

const MenuItem = styled.div`
  margin-bottom: 1.5rem;
`;

const ItemTitle = styled.h4`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const ItemPrice = styled.span`
  color: ${props => props.theme.colors.accent};
  font-weight: bold;
`;

const ItemDescription = styled.p`
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  line-height: 1.4;
`;

const MenuItemTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const MenuItemTag = styled.span`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  font-size: 0.8rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
`;

const WelcomeSection = styled.section`
  padding: 4rem 0;
  text-align: center;
  background: white;
`;

const WelcomeText = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.secondary};
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const BalloonBar = () => {
  return (
    <BalloonBarContainer>
      <SEO 
        title="The Balloon Bar - Exclusive Private Hire Venue"
        description="Host your exclusive event at The Balloon Bar in Belvoir Castle. A stunning historic venue perfect for private functions, corporate events, and special celebrations."
        keywords="Balloon Bar, Belvoir Castle venue, private hire, exclusive events, corporate events, private parties"
        canonicalUrl="https://www.belvoircastle.com/retail-village/venues/balloon-bar"
        ogImage={balloonBar}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "EventVenue",
          "name": "The Balloon Bar at Belvoir Castle",
          "description": "Exclusive private hire venue at Belvoir Castle, perfect for special events and celebrations.",
          "image": balloonBar,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Belvoir Castle",
            "addressLocality": "Grantham",
            "addressRegion": "Leicestershire",
            "postalCode": "NG32 1PE",
            "addressCountry": "UK"
          },
          "telephone": "+441476871001"
        }}
      />
      <ImageHero>
        <VideoOverlay>
          <HeroTitle>The Balloon Bar</HeroTitle>
          <HeroSubtitle>Exclusive Private Hire Venue at Belvoir Castle</HeroSubtitle>
        </VideoOverlay>
        <ScrollIndicator onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24">
            <path d="M12 5L12 19M12 19L5 12M12 19L19 12" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </ScrollIndicator>
      </ImageHero>

      <ContentContainer>
        <WelcomeSection>
          <SectionTitle>Welcome to The Balloon Bar</SectionTitle>
          <WelcomeText>
            The Balloon Bar offers an elegant and exclusive setting for your private events. Located in the heart of Belvoir Castle's retail village, this historic venue combines timeless charm with modern luxury. Available for exclusive hire, it's the perfect backdrop for corporate functions, private celebrations, and special occasions.
          </WelcomeText>
        </WelcomeSection>

        <Section>
          <SectionTitle>Private Hire Information</SectionTitle>
          <InfoGrid>
            <InfoCard>
              <FontAwesomeIcon icon={faUsers} />
              <h3>Capacity</h3>
              <p>Up to 80 guests<br />seated or standing</p>
            </InfoCard>
            <InfoCard>
              <FontAwesomeIcon icon={faGlassCheers} />
              <h3>Event Types</h3>
              <p>Corporate Events<br />Private Parties<br />Special Celebrations</p>
            </InfoCard>
            <InfoCard>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <h3>Availability</h3>
              <p>Available for<br />exclusive hire<br />7 days a week</p>
            </InfoCard>
          </InfoGrid>
        </Section>

        <GallerySection>
          <SectionTitle>Our Venue</SectionTitle>
          <GalleryGrid>
            <GalleryItem $large>
              <GalleryImage src={balloonBar} alt="The Balloon Bar interior" />
              <GalleryOverlay className="overlay">
                <GalleryTitle>Elegant Interior</GalleryTitle>
              </GalleryOverlay>
            </GalleryItem>
            
            <GalleryItem>
              <GalleryImage src={cocktail} alt="Cocktail service" />
              <GalleryOverlay className="overlay">
                <GalleryTitle>Bespoke Drinks Service</GalleryTitle>
              </GalleryOverlay>
            </GalleryItem>
            
            <GalleryItem>
              <GalleryImage src={makingCocktail} alt="Bar service" />
              <GalleryOverlay className="overlay">
                <GalleryTitle>Professional Service</GalleryTitle>
              </GalleryOverlay>
            </GalleryItem>
            
            <GalleryItem>
              <GalleryImage src={castleGin} alt="Premium drinks selection" />
              <GalleryOverlay className="overlay">
                <GalleryTitle>Premium Beverages</GalleryTitle>
              </GalleryOverlay>
            </GalleryItem>
          </GalleryGrid>
        </GallerySection>

        <Section>
          <SectionTitle>Venue Features</SectionTitle>
          <InfoGrid>
            <InfoCard>
              <h3>Facilities</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li>Private Bar</li>
                <li>Sound System</li>
                <li>Flexible Seating</li>
                <li>Air Conditioning</li>
              </ul>
            </InfoCard>
            <InfoCard>
              <h3>Services</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li>Bespoke Drinks Packages</li>
                <li>Catering Options</li>
                <li>Event Planning</li>
                <li>Staff</li>
              </ul>
            </InfoCard>
            <InfoCard>
              <h3>Additional Options</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li>Entertainment</li>
                <li>Decorations</li>
                <li>Photography</li>
    
              </ul>
            </InfoCard>
          </InfoGrid>
        </Section>

        <MembershipSection>
          <MembershipCard>
            <MembershipIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </MembershipIcon>
            <SectionTitle>Make an Enquiry</SectionTitle>
            <IntroText>
              Contact our events team to discuss your requirements and check availability.
            </IntroText>
            <MembershipButton to="/contact/private-hire">
              Enquire Now <FontAwesomeIcon icon={faArrowRight} />
            </MembershipButton>
          </MembershipCard>
        </MembershipSection>
      </ContentContainer>
      <Footer />
    </BalloonBarContainer>
  );
};

export default BalloonBar; 